import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { fetchUserPortfolio, addCompanyToPortfolio, removeCompanyFromPortfolio } from '../services/api';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import '../styles/ModernDashboard.css';
import StockLoader from '../components/StockLoader';
import { useLoader } from '../context/LoaderContext';
import headerLogo from '../assets/branding/Header_logo.png';
import HeaderBackButton from '../components/HeaderBackButton';

const API_URL = process.env.REACT_APP_API_BASE_URL || '/api';
const unwrap = (payload) => (payload && typeof payload === 'object' && 'data' in payload ? payload.data : payload);

const Dashboard = () => {
    const [companies, setCompanies] = useState([]);
    const [search, setSearch] = useState('');
    const [symbolInput, setSymbolInput] = useState('');
    const [loading] = useState(false);
    const [autocompleteResults, setAutocompleteResults] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);

    // Get shared state from layout context
    const context = useOutletContext();
    const isDarkMode = context?.isDarkMode ?? localStorage.getItem('theme') === 'dark';
    const toggleTheme = context?.toggleTheme ?? (() => { });
    const handleAdminOperations = context?.handleAdminOperations ?? (() => { });
    const getUserInitials = context?.getUserInitials ?? (() => 'U');
    const getDisplayName = context?.getDisplayName ?? (() => 'User');
    const getProfilePhotoUrl = context?.getProfilePhotoUrl ?? (() => '');
    const getProfilePhotoLoading = context?.getProfilePhotoLoading ?? (() => false);
    const profilePhotoUrl = getProfilePhotoUrl();
    const profilePhotoLoading = getProfilePhotoLoading();
    const handleMenuOpen = context?.handleMenuOpen ?? (() => { });

    const { show, hide } = useLoader();
    const navigate = useNavigate();
    const autocompleteRef = useRef(null);

    useEffect(() => {
        fetchUserPortfolio()
            .then(setCompanies)
            .catch((err) => {
                setCompanies([]);
                toast.error(err.message || 'Failed to load your portfolio.');
            });
    }, []);

    useEffect(() => {
        document.body.setAttribute('data-theme', isDarkMode ? 'dark' : 'light');
    }, [isDarkMode]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (autocompleteRef.current && !autocompleteRef.current.contains(event.target)) {
                setShowSuggestions(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);


    const handleAddCompany = async () => {
        if (!symbolInput) return;
        const ticker = symbolInput.toUpperCase();
        show();
        try {
            const payload = unwrap(await addCompanyToPortfolio(ticker));
            setCompanies(prev => {
                const id = payload.companyId || payload._id;
                if (prev.some(c => c._id === id || c.companyId === id)) return prev;
                return [...prev, payload];
            });
            toast.success(`${payload.companyName || ticker} added successfully!`);
            setSymbolInput('');
            setAutocompleteResults([]);
            setShowSuggestions(false);
        } catch (err) {
            const msg = err.response?.data?.message || err.response?.data?.detail || err.message || 'Failed to add company.';
            toast.error(msg);
        } finally {
            hide();
        }
    };

    const handleRemoveCompany = async (e, companyId) => {
        e.stopPropagation();
        if (!companyId) return;
        try {
            await removeCompanyFromPortfolio(companyId);
            setCompanies(prev => prev.filter(c => c.companyId !== companyId && c._id !== companyId));
            toast.success(`${companyId} removed from your portfolio.`);
        } catch (err) {
            const msg = err.response?.data?.message || err.response?.data?.detail || err.message || 'Failed to remove company.';
            toast.error(msg);
        }
    };

    const handleAutocompleteChange = async (e) => {
        const value = e.target.value;
        setSymbolInput(value);
        if (value.trim() === '') {
            setAutocompleteResults([]);
            setShowSuggestions(false);
            return;
        }
        if (value.length > 1) {
            try {
                const res = await axios.get(`${API_URL}/finlytics/autocomplete?q=${value}`);
                const payload = unwrap(res.data);
                setAutocompleteResults(Array.isArray(payload) ? payload : []);
                setShowSuggestions(true);
            } catch {
                setAutocompleteResults([]);
                setShowSuggestions(false);
            }
        } else {
            setAutocompleteResults([]);
            setShowSuggestions(false);
        }
    };

    const handleSuggestionClick = (symbol) => {
        setSymbolInput(symbol);
        setAutocompleteResults([]);
        setShowSuggestions(false);
    };

    const filtered = companies.filter(c => {
        const searchTerm = search.toLowerCase();
        return (
            c.companyName?.toLowerCase().includes(searchTerm) ||
            c.sector?.toLowerCase().includes(searchTerm) ||
            c._id?.toLowerCase().includes(searchTerm) ||
            c.companyId?.toLowerCase().includes(searchTerm)
        );
    });

    return (
        <div className="modern-dashboard">
            <ToastContainer position="top-right" autoClose={3000} hideProgressBar theme={isDarkMode ? 'dark' : 'light'} />

            {/* Unified Header */}
            <header className="unified-header">
                <div className="header-brand" onClick={() => navigate('/home')}>
                    <img src={headerLogo} alt="Finlytics Forge" className="header-logo-svg" />
                    <span className="brand__text">
                        <span className="brand__title">Finlytics</span>
                        <span className="brand__subtitle">Forge</span>
                    </span>
                </div>
                <HeaderBackButton to="/home" label="Home" />

                <div className="header-tagline">
                    Personal Stock Portfolio & Deep Financial Research
                </div>

                <div className="header-actions">
                    <button
                        className="action-btn admin-btn"
                        onClick={handleAdminOperations}
                        title="Admin Operations"
                    >
                        ⚙️ Admin
                    </button>
                    <button
                        className="action-btn theme-btn"
                        onClick={toggleTheme}
                        title={isDarkMode ? 'Light Mode' : 'Dark Mode'}
                    >
                        {isDarkMode ? '☀️' : '🌙'}
                    </button>
                    <div className="user-profile" onClick={handleMenuOpen}>
                        <span className="user-name">{getDisplayName()}</span>
                        <div className="user-avatar">
                            {profilePhotoLoading ? (
                                <div className="user-avatar-shimmer" />
                            ) : profilePhotoUrl ? (
                                <img src={profilePhotoUrl} alt={getDisplayName()} className="user-avatar-img" />
                            ) : (
                                getUserInitials()
                            )}
                        </div>
                    </div>
                </div>
            </header>

            <div className="dashboard-container">
                {/* --- Restructured Controls Section --- */}
                <section className="controls-container">
                    <div className="add-company-group" ref={autocompleteRef}>
                        <input
                            type="text"
                            placeholder="Enter symbol or company name (e.g. INFY)"
                            value={symbolInput}
                            onChange={handleAutocompleteChange}
                            className="symbol-input"
                        />
                        <button
                            onClick={handleAddCompany}
                            disabled={loading || !symbolInput}
                            className="add-button"
                        >
                            {loading ? <StockLoader size={18} /> : 'Add Company'}
                        </button>
                        {showSuggestions && autocompleteResults.length > 0 && (
                            <ul className="autocomplete-box">
                                {autocompleteResults.map((item) => {
                                    const symbol = item.url?.split('/')[2];
                                    return (
                                        <li
                                            key={item.id}
                                            onClick={() => handleSuggestionClick(symbol)}
                                            className="autocomplete-item"
                                        >
                                            {item.name} ({symbol})
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>

                    <input
                        type="text"
                        placeholder="Search your portfolio..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="search-bar"
                    />
                </section>

                <section className="company-table-section">
                    <table className="company-table">
                        <thead>
                            <tr>
                                <th>Company Name</th>
                                <th>Sector</th>
                                <th>Exchange ID</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((company) => (
                                <tr
                                    key={company._id || company.companyId}
                                    onClick={() => navigate(`/company/${company._id || company.companyId}`)}
                                    className="company-row"
                                >
                                    <td>{company.companyName}</td>
                                    <td>{company.sector}</td>
                                    <td>{company._id || company.companyId}</td>
                                    <td>
                                        <button
                                            type="button"
                                            className="company-remove-btn"
                                            onClick={(e) => handleRemoveCompany(e, company._id || company.companyId)}
                                        >
                                            Remove
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            {filtered.length === 0 && (
                                <tr>
                                    <td colSpan="4" className="no-results">No results found.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </section>
            </div>
        </div>
    );
};

export default Dashboard;
